import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './style.scss';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { DocumentTemplateSubmission, PatientDocumentAndTemplate } from 'generated/api';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import DocumentTemplate from 'components/HealthRecords/Documents/DocumentTemplate';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { useAppSelector } from 'careand-redux/store';
import { ActivePatient } from 'careand-redux/selectors';
import { usePathParams } from 'components/Navigation/NavStack';
import { useIsLoading, useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import DefaultPopup from 'components/Utils/DefaultPopup';
import CircularProgress from '@material-ui/core/CircularProgress';
import TopBar from 'scenes/HomePatient2/TopBar';
import Icon from 'components/Utils/NewIcon';
import MiscUtils from 'services/MiscUtils';
import { Device } from 'services/Native/Device';

type DocumentTemplateController = {
  getTemplate?: () => Promise<DocumentTemplateSubmission>;
  clearForm?: () => void;
};

const AVERAGE_PAGE_WIDTH = 800;
const TEMPLATE_PAGE_WIDTH = 826;

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.1;

const clampZoom = (value: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));

export default () => {
  const nav = useNavStack(Stacks.Master);
  const { documentId } = usePathParams() as Partial<Record<string, string>>;
  const dispatch = useAppDispatch();
  const controller = useRef<DocumentTemplateController>({});
  const patient = useAppSelector(ActivePatient);
  const sending = useIsLoading(api.Documents.patientFillDocument);
  const savingDraft = useIsLoading(api.Documents.savePatientFillDraft);
  const [documentAndTemplate, setDocumentAndTemplate] = useState<
    PatientDocumentAndTemplate | undefined
  >();
  const { document, template } = documentAndTemplate || ({} as Partial<PatientDocumentAndTemplate>);
  const practitioner = useAppSelector((state) => {
    const pid = document?.physician_id;
    if (!pid) {
      return undefined;
    }
    return state.physicians.byId[pid];
  });
  const isLoadingDocument = useIsLoading(api.Documents.getPatientDocuments);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [status, setStatus] = useState<number>();
  const [lastSavedAt, setLastSavedAt] = useState<number>();

  const isMobile = Device.isMobile();

  // Page navigation / zoom state
  const pageCount = template?.images?.length ?? 0;
  const [currentPage, setCurrentPage] = useState(0);
  const [zoom, setZoom] = useState(1);

  const scrollRef = useRef<HTMLDivElement>(null);
  const transformRef = useRef<ReactZoomPanPinchRef>(null);

  useEffect(() => {
    dispatch(api.Documents.getDocumentPendingPatientFill({ documentId: documentId! }), {
      meta: true,
    }).then((it) => {
      setStatus(it.status);
      setDocumentAndTemplate(it.result);
    });
  }, []);

  const getPageElement = useCallback((index: number): HTMLElement | null => {
    return window.document.querySelector(
      `.patient-template-page .template-page[data-page-index="${index}"]`,
    );
  }, []);

  const goToPage = useCallback(
    (index: number) => {
      const clamped = Math.min(Math.max(index, 0), Math.max(pageCount - 1, 0));
      setCurrentPage(clamped);
      const el = getPageElement(clamped);
      if (!el) return;
      if (isMobile) {
        transformRef.current?.zoomToElement(el, undefined, 300);
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    [pageCount, getPageElement, isMobile],
  );

  // Keep the page selector in sync with native scrolling on desktop.
  useEffect(() => {
    if (isMobile) return;
    const container = scrollRef.current;
    if (!container || !document) return;

    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const containerTop = container.getBoundingClientRect().top;
        let best = 0;
        let bestDistance = Infinity;
        for (let i = 0; i < pageCount; i++) {
          const el = getPageElement(i);
          if (!el) continue;
          const distance = Math.abs(el.getBoundingClientRect().top - containerTop);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = i;
          }
        }
        setCurrentPage(best);
      });
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [isMobile, document, pageCount, getPageElement]);

  const onSaveProgress = async () => {
    const submission = controller.current?.getTemplate
      ? await controller.current.getTemplate()
      : undefined;

    if (!submission) {
      return;
    }

    const { isSuccess } = await dispatch(
      api.Documents.savePatientFillDraft({
        documentId: documentId!,
        documentTemplateSubmission: submission,
      }),
      { meta: true, errorTitle: 'Save Progress' },
    );

    if (isSuccess) {
      setLastSavedAt(Date.now());
      MiscUtils.showSuccess('Progress saved', 'You can finish this document later.');
    }
  };

  const onClickSubmit = () => {
    setShowConfirmationModal(true);
  };

  const onConfirmSubmit = async () => {
    const submission = controller.current?.getTemplate
      ? await controller.current.getTemplate()
      : undefined;

    if (!submission) {
      return;
    }

    const { isSuccess } = await dispatch(
      api.Documents.patientFillDocument({
        documentId: documentId!,
        documentTemplateSubmission: submission,
      }),
      { meta: true },
    );

    if (isSuccess) {
      // Clear the persisted draft so a re-open (when re-filling is allowed)
      // starts from a clean slate instead of stale local values.
      controller.current?.clearForm?.();
      nav.pop();
    }
  };

  const fitToWidth = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const available = container.clientWidth - 32; // account for padding
    setZoom(clampZoom(available / TEMPLATE_PAGE_WIDTH));
  }, []);

  const mobileScale = (window.document.body.clientWidth / AVERAGE_PAGE_WIDTH) * 0.9;

  const showError = status && status > 200;
  const isLoading = !document && !showError;
  const showDocument = !!document && !showError;
  const showActions = !isLoadingDocument && showDocument;

  const documentTemplate = document && (
    <DocumentTemplate
      noZoom
      hideDocumentPanel
      hideAddPage
      templateOnly
      templateId={document?.template_id}
      template={template}
      document={document}
      onClose={() => {}}
      patient={patient}
      physician={practitioner}
      defaultSavedDocumentCategory={undefined}
      onSaveCompleted={() => {}}
      prefilledValues={undefined}
      editMode={false}
      documentTemplateController={controller.current}
    />
  );

  const pageIndicator = showActions && pageCount > 1 && (
    <div className="page-indicator">
      <DefaultButton
        alt="Previous page"
        icon="chevron-up"
        className="white nav-button"
        disabled={currentPage <= 0}
        onClick={() => goToPage(currentPage - 1)}
      />
      <span className="page-label">
        Page {currentPage + 1} / {pageCount}
      </span>
      <DefaultButton
        alt="Next page"
        icon="chevron-down"
        className="white nav-button"
        disabled={currentPage >= pageCount - 1}
        onClick={() => goToPage(currentPage + 1)}
      />
    </div>
  );

  const zoomControls = showActions && !isMobile && (
    <div className="zoom-controls">
      <DefaultButton
        alt="Zoom out"
        icon="magnifying-glass-minus"
        className="white nav-button"
        disabled={zoom <= ZOOM_MIN}
        onClick={() => setZoom((z) => clampZoom(z - ZOOM_STEP))}
      />
      <span className="zoom-level">{Math.round(zoom * 100)}%</span>
      <DefaultButton
        alt="Zoom in"
        icon="magnifying-glass-plus"
        className="white nav-button"
        disabled={zoom >= ZOOM_MAX}
        onClick={() => setZoom((z) => clampZoom(z + ZOOM_STEP))}
      />
      <DefaultButton
        alt="Fit to width"
        icon="arrows-left-right"
        className="white nav-button"
        onClick={fitToWidth}
      />
    </div>
  );

  return (
    <div className="page patient-template-page" data-force-theme="light">
      <TopBar title="Fill Document" />

      {(pageIndicator || zoomControls) && (
        <div className="controls-bar">
          {pageIndicator}
          {zoomControls}
        </div>
      )}

      {isLoading && (
        <div className="loading-state">
          <div className="loading-spinner-wrap">
            <CircularProgress size={52} thickness={3.5} className="loading-spinner" />
            <Icon src="file-lines" width="20px" className="loading-glyph" />
          </div>
          <div className="loading-title">Loading your document…</div>
          <div className="loading-subtitle">Hang tight, this will only take a moment</div>
        </div>
      )}
      {showError && (
        <div className="no-longer-available w-full h-full flex justify-center flex-col relative top-[-10%] gap-5 p-5">
          <Icon src="circle-exclamation" width="50px" className="red self-center" />
          <span className="text-center">The Document is no longer available</span>
          <DefaultButton name="OK" onClick={() => nav.popTo('/')} />
        </div>
      )}

      {showDocument &&
        (isMobile ? (
          <div className="mobile-viewer">
            <TransformWrapper
              ref={transformRef}
              limitToBounds
              centerZoomedOut
              // Only exclude the interactive fields so taps focus them; the
              // document body is an <img>, so excluding 'img' would make most
              // of the surface un-pannable (feels like hitting a wall).
              panning={{ excluded: ['input', 'textarea', 'select'] }}
              doubleClick={{ disabled: true }}
              minScale={mobileScale}
              initialScale={mobileScale}
            >
              <TransformComponent
                wrapperClass="ptpl-transform-wrapper"
                contentClass="ptpl-transform-content"
              >
                {documentTemplate}
              </TransformComponent>
            </TransformWrapper>
          </div>
        ) : (
          <div className="desktop-viewer scroll-list" ref={scrollRef}>
            <div className="zoom-wrapper" style={{ zoom } as React.CSSProperties}>
              {documentTemplate}
            </div>
          </div>
        ))}

      {showActions && (
        <div className="toolbar">
          {lastSavedAt && (
            <span className="last-saved">
              <Icon src="circle-check" width="14px" className="green" /> Progress saved
            </span>
          )}
          <DefaultButton
            name="Save Progress"
            className="light"
            icon="floppy-disk"
            onClick={onSaveProgress}
            loading={savingDraft}
            disabled={sending}
          />
          <DefaultButton
            name="Save & Submit"
            className="blue"
            icon="paper-plane"
            onClick={onClickSubmit}
            loading={sending}
            disabled={savingDraft}
          />
        </div>
      )}

      <DefaultPopup
        className="patient-template-popup"
        title="Confirm & Submit"
        openHandler={[showConfirmationModal, setShowConfirmationModal]}
      >
        <div className="content">
          <div className="prompt">
            Are you sure you&apos;re ready to submit the document? You won&apos;t be able to edit it
            after submitting.
          </div>
          <div className="buttons">
            <DefaultButton name="Yes, Submit" onClick={onConfirmSubmit} loading={sending} />
            <DefaultButton
              name="Not yet"
              className="light"
              onClick={() => setShowConfirmationModal(false)}
              disabled={sending}
            />
          </div>
        </div>
      </DefaultPopup>
    </div>
  );
};
